import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import {
  MANAGERS_KEY,
  REQUESTED_EMPLOYEE_KEY,
  EMPLOYEES_KEY,
  POND_CARETAKERS,
  getFarmGroupKey,
  getFeaturesKey,
  getMembersKey,
  getRolesKey,
  getPresetKey,
} from 'common/constants/queryKeys'
import { useUserInfo } from 'modules/user/api/queries'
import { useRecoilValue } from 'recoil'

import {
  getEmployees,
  getFeatures,
  getManagers,
  getMembers,
  getPondCaretakers,
  getRequestedEmployees,
  getRoles,
  getRolesPreset,
  getFarmsGroup,
} from '.'
import { employeeTableFilterState } from '../stores/atom'

const ROWS_PER_PAGE = 10

// Deprecated
export const useGetManagers = () => {
  return useQuery({
    queryKey: MANAGERS_KEY,
    queryFn: async () => {
      const { data } = await getManagers()
      return data.data
    },
    staleTime: Infinity,
    enabled: false,
  })
}

// Deprecated
export const useGetEmployees = () => {
  const tableFilter = useRecoilValue(employeeTableFilterState)
  const initParams = { pageSize: ROWS_PER_PAGE, page: 1, ...tableFilter }

  return useInfiniteQuery({
    queryKey: EMPLOYEES_KEY,
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getEmployees(pageParam)

      return {
        employees: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage, _) => {
      if (lastPage.nextPage) {
        return { ...initParams, page: lastPage.nextPage }
      } else {
        return undefined
      }
    },
    staleTime: Infinity,
  })
}

// Deprecated
export const useGetRequestedEmployees = () => {
  return useQuery({
    queryKey: REQUESTED_EMPLOYEE_KEY,
    queryFn: async () => {
      const { data } = await getRequestedEmployees()
      return data.data
    },
    staleTime: Infinity,
  })
}

// Deprecated
export const useGetPondCaretakers = () => {
  return useQuery({
    queryKey: POND_CARETAKERS,
    queryFn: async () => {
      const { data } = await getPondCaretakers()
      return data.data
    },
    staleTime: Infinity,
    enabled: false,
  })
}

// NK-252: หน้าพนักงาน used to pull every member in the organization in one request and
// filter it in the browser. It now pages through the list and pushes the search term
// down to the backend, so search still covers members that are not yet loaded.
export const useGetMembers = (search = '') => {
  const { data: userInfo } = useUserInfo()

  const initParams = {
    search,
    page: 1,
    pageSize: ROWS_PER_PAGE,
  }

  return useInfiniteQuery({
    queryKey: getMembersKey(userInfo?.id, search),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getMembers({
        userId: userInfo?.id || '',
        params: { ...pageParam },
      })

      return {
        members: data.data,
        nextPage: data.nextPage,
        totalItems: data.totalItems,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return { ...initParams, page: lastPage.nextPage }
      }
      return undefined
    },
    staleTime: Infinity,
    enabled: !!userInfo,
  })
}

export const useGetRoles = () => {
  const { data: userInfo } = useUserInfo()

  return useQuery({
    queryKey: getRolesKey(userInfo?.id),
    queryFn: async () => {
      const { data } = await getRoles({ userId: userInfo?.id || '' })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!userInfo,
  })
}

export const useGetRolesPreset = () => {
  const { data: userInfo } = useUserInfo()
  return useQuery({
    queryKey: getPresetKey(userInfo?.id),
    queryFn: async () => {
      const { data } = await getRolesPreset({ userId: userInfo?.id || '' })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!userInfo,
  })
}

export const useGetFeatures = () => {
  const { data: userInfo } = useUserInfo()
  return useQuery({
    queryKey: getFeaturesKey(userInfo?.id),
    queryFn: async () => {
      const { data } = await getFeatures({ userId: userInfo?.id || '' })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!userInfo,
  })
}

export const useGetFarmGroup = (search: string | null) => {
  const initParams = {
    name: search || '',
    page: 1,
    pageSize: ROWS_PER_PAGE,
  }

  return useInfiniteQuery({
    queryKey: getFarmGroupKey(search || ''),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await getFarmsGroup({
        name: search!,
        params: {
          ...pageParam,
        },
      })

      return {
        farmGroups: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return { ...initParams, page: lastPage.nextPage }
      }
      return undefined
    },
    staleTime: Infinity,
  })
}
